import { z } from "zod";
import { mcpContextIdsSchema, mcpIntegrationIdsSchema } from "./copilot-mcp.js";

const responseStyle = z.enum(["precise", "balanced", "creative"]);
const responseLength = z.enum(["brief", "normal", "detailed"]);
/** `auto` or a language tag — kept loose because it is only ever echoed into
 *  the instruction preamble, never resolved against a locale table. */
const responseLanguage = z.string().trim().min(1).max(20);
/**
 * The standing instructions a chat runs under.
 *
 * Named `instructions`, not "system prompt": there is no system role here. The
 * text is sent as `additionalContext` — free grounding material the model
 * reasons over alongside the prompt — so it shapes HOW an answer is written and
 * is NOT a privileged channel or a security boundary. The old name invited
 * exactly the wrong assumption.
 */
const instructions = z.string().trim().max(4_000);

/**
 * Which Copilot API a turn goes to.
 *
 * A DEPLOYMENT choice, expressed only by `COPILOT_API`. It was once a per-user
 * override too, and that is deliberately gone: the two APIs need different
 * admin-consented permissions, so a user switching adapters could land on a
 * resource their tenant had never granted and get a consent error they had no
 * way to act on. The schema stays because the value is still carried — on a
 * conversation, recording which API minted its upstream id.
 */
export const copilotApiSchema = z.enum(["workiq", "graph"]);


/**
 * A GUID, in the bare form KQL's `SiteID` property matches.
 *
 * Graph reports a site's id as a COMPOSITE — `contoso.sharepoint.com,<site
 * guid>,<web guid>` — and KQL wants the middle segment on its own. Both forms
 * are accepted here and normalized to the bare GUID, because the value the
 * picker has to hand is Graph's and the value the filter needs is not.
 */
const siteIdSchema = z
  .string()
  .trim()
  .min(1)
  .max(200)
  .transform((value, ctx) => {
    const guid = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
    const parts = value.split(",").map((part) => part.trim());
    // A composite id is host,site,web — the SITE guid is the one KQL filters on,
    // so the FIRST guid is taken rather than the last.
    const found = parts.find((part) => guid.test(part)) ?? (guid.test(value) ? value : null);
    const matched = found?.match(guid)?.[0];
    if (!matched) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Expected a SharePoint site GUID, or Graph's composite site id.",
      });
      return z.NEVER;
    }
    return matched.toLowerCase();
  });

/**
 * A SharePoint or OneDrive URL prefix for the `Path:` filter.
 *
 * Constrained to an absolute https URL with no double quote in it. The quote
 * matters more than it looks: the value is interpolated into a KQL string
 * literal, and a stray quote would not raise an error — the Retrieval API
 * responds to malformed KQL by running the query with NO SCOPING AT ALL, which
 * silently widens a question the user deliberately narrowed. Refusing the input
 * is the only way to keep "I asked one site" from meaning "I asked everything".
 *
 * SPACES are allowed, and must be: Microsoft's guidance is to take this path
 * from the item's Details pane, and that path reads
 * `https://…/Shared Documents/…` — the form the search index actually holds.
 * Rejecting it forced every caller to send the percent-encoded `webUrl`
 * instead, which matches nothing. A space inside a quoted KQL literal has no
 * syntactic meaning; the quote and the line break are the characters that do.
 */
const sharePointPathSchema = z
  .string()
  .trim()
  .min(1)
  .max(2_000)
  .refine((value) => /^https:\/\/[^"\r\n\t]+$/i.test(value), {
    message: "Expected an absolute https:// path, with no quotes or line breaks.",
  });

/** Copilot connector ids are alphanumeric, per the connector schema rules. */
const connectionIdSchema = z.string().trim().regex(/^[A-Za-z0-9]{1,32}$/);

const knowledgeTimeRangeSchema = z.discriminatedUnion("preset", [
  z.object({ preset: z.enum(["hour", "day", "week", "month", "year"]) }),
  z.object({
    preset: z.literal("custom"),
    from: z.string().datetime({ offset: true }),
    to: z.string().datetime({ offset: true }),
  }),
]).refine((range) => range.preset !== "custom" || Date.parse(range.from) <= Date.parse(range.to), {
    message: "The custom time range must end after it starts.",
  });

/**
 * What a question is asked against.
 *
 * A discriminated union rather than a pair of loose optional fields, so that
 * "a site scope with no site id" is unrepresentable instead of being a runtime
 * check somebody forgets. Each narrowing kind carries an identifier the backend
 * turns into a KQL `filterExpression`; none of them carries free text destined
 * for the prompt.
 */
export const knowledgeScopeSchema = z.discriminatedUnion("kind", [
  // `all` is Work IQ's native grounding and runs NO retrieval - see
  // `KnowledgeScope` for why that, and not a two-source retrieval, is what
  // "All Microsoft 365" means.
  z.object({ kind: z.literal("all") }),
  z.object({ kind: z.literal("mail"), timeRange: knowledgeTimeRangeSchema }),
  z.object({ kind: z.literal("teams"), timeRange: knowledgeTimeRangeSchema }),
  z.object({ kind: z.literal("sharePoint") }),
  z.object({
    kind: z.literal("sharePointSite"),
    siteId: siteIdSchema,
    /** Display only — echoed back for the picker and for citations. */
    siteName: z.string().trim().max(200).nullish(),
    siteUrl: z.string().trim().max(2_000).nullish(),
  }),
  z.object({
    kind: z.literal("sharePointPath"),
    path: sharePointPathSchema,
    /** The folder's site, when the picker knew it — see `KnowledgeScope`. */
    siteId: siteIdSchema.nullish(),
    /**
     * Graph's ids for the library and the folder. Opaque strings rather than a
     * shape worth validating: they are echoed back to Graph in a URL path, and
     * an id Graph does not recognize is a 404 from Graph, not a filter that
     * silently widens.
     */
    driveId: z.string().trim().min(1).max(300).nullish(),
    itemId: z.string().trim().min(1).max(300).nullish(),
    label: z.string().trim().max(200).nullish(),
  }),
  z.object({ kind: z.literal("oneDrive") }),
  z.object({
    kind: z.literal("oneDrivePath"),
    path: sharePointPathSchema,
    /**
     * Graph's ids for the user's drive and the folder inside it. No `siteId`:
     * a personal OneDrive has no site to narrow through — see `KnowledgeScope`.
     */
    driveId: z.string().trim().min(1).max(300).nullish(),
    itemId: z.string().trim().min(1).max(300).nullish(),
    label: z.string().trim().max(200).nullish(),
  }),
  z.object({
    kind: z.literal("externalItem"),
    connectionIds: z.array(connectionIdSchema).max(10).optional(),
  }),
]);

/**
 * Documents pinned to a turn as `contextualResources.files`.
 *
 * Capped at five. Microsoft documents no limit, and an uncapped list is a way
 * to build a request large enough to time out; five covers "compare these
 * documents" without becoming a file manager.
 */
export const copilotContextFileSchema = z.object({
  uri: z.string().trim().url().max(2_000),
});

export const copilotChatSchema = z.object({
  message: z.string().trim().min(1).max(8_000),
  /** OUR conversation id (a UUID), not Graph's. Omitted to start a new chat. */
  conversationId: z.string().uuid().optional(),
  timeZone: z.string().trim().min(1).max(100).default("UTC"),
  /** Re-aims THIS turn, and the conversation with it. */
  knowledgeScope: knowledgeScopeSchema.optional(),
  agentId: z.string().uuid().nullable().optional(),
  files: z.array(copilotContextFileSchema).max(5).optional(),
  /**
   * Completed MCP retrievals to ground this turn with — see
   * `mcpContextIdsSchema`. IDS ONLY: the tool result stays server-side, so
   * nothing here can be used to inject fabricated business evidence into a
   * question.
   */
  mcpContextIds: mcpContextIdsSchema.optional(),
  /**
   * Business integrations selected in the composer for this turn.
   * Presence is significant: an empty array means no business source was
   * selected. Whether that selection is exclusive comes from the user's saved
   * `mcpOnly` setting and is enforced server-side.
   */
  mcpIntegrationIds: mcpIntegrationIdsSchema.optional(),
});

/** Context for generating the empty-chat starter prompts. */
export const copilotStarterSuggestionsSchema = z.object({
  timeZone: z.string().trim().min(1).max(100).default("UTC"),
  knowledgeScope: knowledgeScopeSchema.optional(),
});

/**
 * The retrieval self-test: one question, run at several widths.
 *
 * Takes the same scope shape a chat turn does, so the test asks exactly what
 * the failing question asked — a self-test that reformulated the scope would be
 * testing something other than the thing that failed.
 */
export const copilotKnowledgeProbeSchema = z.object({
  question: z.string().trim().min(1).max(1_500),
  knowledgeScope: knowledgeScopeSchema.optional(),
});

export const copilotSettingsSchema = z
  .object({
    instructions,
    responseStyle,
    responseLength,
    responseLanguage,
    webGrounding: z.boolean(),
    /** Empty string and null both mean "follow the browser". */
    timeZone: z.string().trim().max(100).nullable(),
    retainTranscripts: z.boolean(),
    /** If true, selected MCP integrations are a hard source boundary. */
    mcpOnly: z.boolean(),
    // No `api`: which Copilot API a turn goes to is a deployment choice
    // (`COPILOT_API`) and not a per-user setting, so a PATCH carrying one is
    // stripped here and never reaches the store.
    knowledgeScope: knowledgeScopeSchema,
  })
  .partial()
  .refine((value) => Object.keys(value).length > 0, {
    message: "Provide at least one setting to update.",
  });

/** New conversations may override any part of the profile up front. */
export const createCopilotConversationSchema = z.object({
  title: z.string().trim().min(1).max(200).optional(),
  instructions: instructions.optional(),
  responseStyle: responseStyle.optional(),
  responseLength: responseLength.optional(),
  responseLanguage: responseLanguage.optional(),
  webGrounding: z.boolean().optional(),
  knowledgeScope: knowledgeScopeSchema.optional(),
  agentId: z.string().uuid().nullable().optional(),
});

/** Rename, pin, archive, or re-aim an existing conversation. */
export const updateCopilotConversationSchema = createCopilotConversationSchema
  .extend({
    isPinned: z.boolean().optional(),
    isArchived: z.boolean().optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: "Provide at least one field to update.",
  });

export const copilotSavedPromptSchema = z.object({
  name: z.string().trim().min(1).max(80),
  prompt: z.string().trim().min(1).max(4_000),
});

export const updateCopilotSavedPromptSchema = copilotSavedPromptSchema
  .partial()
  .refine((value) => Object.keys(value).length > 0, {
    message: "Provide a name or a prompt to update.",
  });

// The thinking-model schemas lived here until migration 0059 merged the
// profile into the agent. An agent is validated by `agentSchema` in
// validation/src/agents.ts, which now carries `knowledgeScope` too, and is
// written through /agent-admin rather than the Copilot module.

export type KnowledgeScopeInput = z.infer<typeof knowledgeScopeSchema>;
export type CopilotChatInput = z.infer<typeof copilotChatSchema>;
export type CopilotStarterSuggestionsInput = z.infer<typeof copilotStarterSuggestionsSchema>;
export type CopilotSettingsInput = z.infer<typeof copilotSettingsSchema>;
export type CreateCopilotConversationInput = z.infer<
  typeof createCopilotConversationSchema
>;
export type UpdateCopilotConversationInput = z.infer<
  typeof updateCopilotConversationSchema
>;
export type CopilotSavedPromptInput = z.infer<typeof copilotSavedPromptSchema>;
export type UpdateCopilotSavedPromptInput = z.infer<typeof updateCopilotSavedPromptSchema>;

/**
 * Hosts an answer's inline images may be fetched from on the reader's behalf.
 *
 * A Copilot answer can embed an image it produced (a chart, a rendered table)
 * or one it found, and the URL it writes into the Markdown points at Microsoft's
 * own attachment storage — `ch-prod.asyncgw.teams.microsoft.com/v1/objects/...`
 * for something Copilot attached to the turn, SharePoint or OneDrive for a
 * document's rendition. Two separate things then stop the browser from showing
 * it: the SPA's `img-src 'self' data:` policy refuses any third-party image
 * outright, and these objects are not public — an unauthenticated GET gets a
 * 401, which is why the alt text renders and the picture never does.
 *
 * Both are answered by fetching the image through the API instead (see
 * `copilot.images.service.ts`), which makes it a same-origin image and lets the
 * caller's own delegated token be presented for it. That turns the API into a
 * fetcher of URLs chosen by an upstream service, so the set of hosts it will
 * fetch from is an explicit allowlist rather than "anything https": every entry
 * is Microsoft-operated storage that a Copilot answer legitimately points at,
 * and an image hosted anywhere else is shown as a link instead of proxied.
 *
 * Matched as a host or as a subdomain of one — never as a substring, which
 * would accept `sharepoint.com.example.net`.
 */
export const COPILOT_IMAGE_HOSTS = [
  // Copilot/Teams attachment storage — where an image Copilot generated lands.
  "asyncgw.teams.microsoft.com",
  "teams.microsoft.com",
  "graph.microsoft.com",
  "substrate.office.com",
  "sharepoint.com",
  // OneDrive/SharePoint file renditions and thumbnails.
  "svc.ms",
  // Thumbnails for web-grounded answers, served by Microsoft rather than by
  // the site the answer cites.
  "bing.com",
  "bing.net",
] as const;

/** Whether one host is an allowlisted image host or a subdomain of one. */
export function copilotImageHostAllowed(host: string): boolean {
  const name = host.trim().toLowerCase().replace(/\.$/, "");
  return COPILOT_IMAGE_HOSTS.some(
    (allowed) => name === allowed || name.endsWith(`.${allowed}`),
  );
}

/**
 * Whether an image URL out of an answer may be fetched through the API.
 *
 * HTTPS only, and an allowlisted host only. Everything else — a `data:` image,
 * which the browser renders unaided, and any other origin — is false, and the
 * caller shows a link rather than an image.
 */
export function isProxyableCopilotImage(raw: string): boolean {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return false;
  }
  return url.protocol === "https:" && copilotImageHostAllowed(url.hostname);
}
